/**
 * @fileoverview Tenant sync collection configuration — the authoritative
 *               mapping between tenant-web Dexie table names and tenant
 *               MongoDB collection names, plus the push allowlist and the
 *               system-derived (read-only via /system-sync) projections.
 * @module sync-config/tenant
 *
 * Responsibilities:
 * - Define `TENANT_COLLECTION_MAP` (Dexie key → tenant MongoDB collection).
 * - Define `TENANT_PUSH_ALLOWED_COLLECTIONS` (which Dexie keys may push).
 * - Define `TENANT_SYSTEM_DERIVED_COLLECTION_MAP` (Dexie key → system MongoDB
 *   collection, accessed via tenant-api `/system-sync`).
 * - Define `TENANT_SYSTEM_DERIVED_PUSH_ALLOWED_COLLECTIONS` for guarded
 *   system-owned writes that still originate from tenant-web local Dexie.
 * - Define `TENANT_COLLECTION_DISPLAY_NAMES` for sync UI surfaces.
 *
 * Dependencies:
 * - None — pure configuration.
 *
 * Architecture:
 * - `/sync` (tenant DB) uses `TENANT_COLLECTION_MAP` + `TENANT_PUSH_ALLOWED`.
 * - `/system-sync` (system DB tenant projection) uses
 *   `TENANT_SYSTEM_DERIVED_COLLECTION_MAP`; pushes are limited to explicitly
 *   guarded collections in `TENANT_SYSTEM_DERIVED_PUSH_ALLOWED_COLLECTIONS`.
 * - The tenant-api routes import these maps directly; drift is impossible
 *   without changing this file.
 *
 * Notes:
 * - importTemplates and importRuns are NOT tenant-owned (Phase-1 import
 *   centralization). Tenants project them via `system_import_templates`
 *   and `system_import_runs` instead. See `Docs/IMPORT-CENTRALIZATION-DEFERRED.md`.
 */

// ============================================================================
// Tenant Sync Collection Configuration
// ----------------------------------------------------------------------------
// ROLE
//   Single source of truth for tenant-web collection mappings between Dexie
//   table names (client) and MongoDB collection names (server).
//
// DATA FLOW — [PHASE-1] Import centralization
//   • importTemplates and importRuns are NO LONGER tenant-owned collections.
//     They have been removed from TENANT_COLLECTION_MAP and from
//     TENANT_PUSH_ALLOWED_COLLECTIONS.
//   • Tenants project system-owned importTemplates via
//     TENANT_SYSTEM_DERIVED_COLLECTION_MAP as system_import_templates.
//   • Tenants project their own runs via system_import_runs (new in
//     phase 2 of this centralization — tenant-scoped pull from system DB).
//
// IMPORTANT: When adding a new tenant-owned collection:
//   1. Add entry to TENANT_COLLECTION_MAP
//   2. Add to TENANT_PUSH_ALLOWED_COLLECTIONS if client should push changes
//   3. Add display name to TENANT_COLLECTION_DISPLAY_NAMES
//   4. Update Dexie schema in web/packages/local-db/src/databases/tenantDb.ts
//
// DEFERRED / FUTURE WORK
//   [PHASE-2] If tenant-authored private import templates are later permitted,
//     add system_import_templates to TENANT_SYSTEM_DERIVED_PUSH_ALLOWED so
//     Dexie changes sync back up via /system-sync push.
//     See Docs/IMPORT-CENTRALIZATION-DEFERRED.md §4.
//
// RELATED FILES
//   e:\gitrepo\FireSight\FireSight\api\tenant-api\src\routes\systemSyncRoutes.ts
//   e:\gitrepo\FireSight\FireSight\api\tenant-api\src\sync\systemLibraryVisibility.ts
//   e:\gitrepo\FireSight\FireSight\web\packages\local-db\src\databases\tenantDb.ts
// ============================================================================

/**
 * Tenant collection map — Dexie table name → server collection name
 * Used for both HTTP sync (/api/sync/pull, /api/sync/push) and WebSocket sync
 */
export const TENANT_COLLECTION_MAP = {
  entities: 'inspectableEntities',
  cases: 'inspectionCases',
  contacts: 'contacts',
  templates: 'templates',
  forms: 'forms',
  violation_code_sets: 'violationCodeSets',
  violation_chapters: 'violationCodeSetChapters',
  violation_index: 'violationIndex',
  inspection_accounts: 'inspectionAccounts',
  inspection_account_groups: 'inspectionAccountGroups',
  roles: 'roles',
  service_area_types: 'serviceAreaTypes',
  service_area_values: 'serviceAreaValues',
  lookup_values: 'lookups',
  lookup_groups: 'lookupGroups',
  lookup_categories: 'lookupCategories',
  // user_groups: server-managed by system administrators. Clients pull this
  // list for display and filtering; tenants must not mutate it directly.
  user_groups: 'userGroups',
  // [D-5] users intentionally absent. All user records live in the SYSTEM DB.
  // Tenant-web reads/writes department users via:
  //   system_users_ref (Dexie) → TENANT_SYSTEM_DERIVED_COLLECTION_MAP → /system-sync
  // The /system-sync pull filters by departmentMemberships.departmentId so each
  // tenant only sees its own users. Push goes through /system-sync/push which
  // writes to the system DB, not the tenant DB. There is no tenant DB `users`
  // collection in active use.
  // [PHASE-1] import_templates and import_runs are NOT tenant-owned collections.
  // They live exclusively in the system DB; tenant-web sees them via
  // /system-sync as system_import_templates / system_import_runs.
  report_definitions: 'reportDefinitions',
  report_templates: 'reportTemplates',
  report_schedules: 'reportSchedules',
  uploaded_files: 'uploadedFiles',
  generated_documents: 'generatedReports',
  email_templates: 'emailTemplates',
  email_delivery_logs: 'emailDeliveryLogs',
  mail_configurations: 'mailConfigurations',
  mail_jobs: 'mailJobs',
  inspection_delivery_records: 'inspectionDeliveryRecords',
  inspection_delivery_groups: 'inspectionDeliveryGroups',
  inspection_delivery_events: 'inspectionDeliveryEvents',
  // inspection_print_batches: tenant-owned bulk-print batch tracking (#143 P2).
  inspection_print_batches: 'inspectionPrintBatches',
  // generated_document_versions: tenant-owned immutable per-send report version
  // references (#144 P5). Legal evidence of the exact report sent; never rewritten.
  generated_document_versions: 'generatedDocumentVersions',
  // inspection_sms_deliveries: tenant-owned append-only ledger of ACS SMS send
  // occurrences (#144 P6). Flag-gated (`sms`, default off); ships last.
  // DEFERRED TO FUTURE DEVELOPMENT (2026-06): client ledger only — the server
  // ACS send worker is not built, so occurrences stay queued until it is wired.
  inspection_sms_deliveries: 'inspectionSmsDeliveries',
  // inspection_postage_usage: tenant-owned append-only stamps.com postage/label
  // usage ledger for provider_mail cost allocation (#144 P6). Flag-gated
  // (`stampsCom`, default off). DEFERRED TO FUTURE DEVELOPMENT (2026-06): client
  // ledger only — the server stamps.com label/postage/tracking integration is
  // not built, so entries stay `pending` until that worker is wired.
  inspection_postage_usage: 'inspectionPostageUsage',
  // inspector_locations: pull-only. The mobile-pwa writes location updates
  // through a dedicated real-time tracking endpoint, not via sync push. The
  // sync pull path makes current positions available to tenant-web map views.
  inspector_locations: 'inspectorLocations',
  // audit_logs: append-only audit trail. Push-only from client; pulled
  // back rarely. Required here so the WS validator (which checks against
  // Object.values(TENANT_COLLECTION_MAP)) doesn't reject `auditLogs` push
  // requests with INVALID_COLLECTION.
  audit_logs: 'auditLogs',
  // background_jobs: server-authoritative. Workers write job status/progress;
  // clients read via sync. NOT in TENANT_PUSH_ALLOWED_COLLECTIONS. The Mongo
  // change stream on `backgroundJobs` becomes the real-time progress channel
  // for the tether indicator flyout in tenant-web.
  background_jobs: 'backgroundJobs',
  // fee_schedules: server-authoritative. System-api AI parse worker writes
  // extracted fee rules; clients pull-only for the fee knowledge base.
  // NOT in TENANT_PUSH_ALLOWED_COLLECTIONS.
  fee_schedules: 'feeSchedules',
  // field_definitions: user-defined fields configuration per entity target collection.
  field_definitions: 'fieldDefinitions',
  occupancy_chemicals: 'occupancyChemicals',
  occupancy_tanks: 'occupancyTanks',
  // complex_entity_groups: Complex Inspections Phase 1B — pull-only. The
  // tenant-api complex-mutation boundary is the ONLY writer; ordinary sync
  // push can never carry this collection, so it is deliberately absent from
  // TENANT_PUSH_ALLOWED_COLLECTIONS.
  complex_entity_groups: 'inspectableEntityGroups',
  // Inspection Programs & Entity Scheduling (#737 Phase 1).
  // inspection_programs: tenant-owned (custom programs, tenant hidden/active
  //   flags). Pushable — tenant writes hide/unhide/deactivate/delete/custom.
  // auto_schedule_rules / carry_forward_rules: tenant-owned rule config
  //   (seeded with defaults at provisioning; tenants toggle/configure).
  // schedule_execution_runs / schedule_execution_evidence: server-authoritative
  //   worker-written history — pull-only, NOT in TENANT_PUSH_ALLOWED.
  inspection_programs: 'inspectionPrograms',
  auto_schedule_rules: 'autoScheduleRules',
  carry_forward_rules: 'carryForwardRules',
  schedule_execution_runs: 'scheduleExecutionRuns',
  schedule_execution_evidence: 'scheduleExecutionEvidence',
  // schedule_rule_catalog (#737 Phase 6, plan §11.3): system-managed rule-type
  // catalog replayed read-only into the tenant DB — the Auto-Schedule Rules
  // page reads it to populate the rule type dropdown and available
  // conditions/actions. Pull-only; NOT in TENANT_PUSH_ALLOWED_COLLECTIONS.
  schedule_rule_catalog: 'scheduleRuleCatalog',
  // Customer Portal Phase 2 — portal_requests / portal_change_requests are
  // tenant-owned but written through dedicated boundaries, never via generic
  // sync push:
  //   portal_requests — written by customer-portal-api (draft/submit/reply) and
  //     tenant-api /api/portal/field-requests/* (assign/review/reply). Pull-only.
  //   portal_change_requests — written by customer-portal-api submit and
  //     tenant-api /api/portal/pending-changes/:id/approve|reject. Pull-only.
  portal_requests: 'portalRequests',
  portal_change_requests: 'portalChangeRequests',
  // Customer Portal Phase 2b — portalActivityLog is written only by
  // customer-portal-api (recordPortalActivity). Pull-only read surface for
  // the tenant-web Customer Portal admin pages (Overview/Activity Log).
  portal_activity_log: 'portalActivityLog',
  // Customer Portal Phase 3 — selfCertificationSubmissions are written by
  // customer-portal-api (wizard/repair evidence) and reviewed through the
  // dedicated /api/portal/self-cert-reviews/:id/review boundary. Pull-only.
  self_certification_submissions: 'selfCertificationSubmissions',
  // Customer Portal Phase 6 — publicSafetySurveys are written by
  // customer-portal-api's anonymous survey submit (find-or-create entity +
  // upsert-by-entityId). Pull-only — the dispatch map + public education
  // admin page read them; staff never edit them through generic sync.
  public_safety_surveys: 'publicSafetySurveys',
  // customerQuestions — customer↔inspector threads written by
  //   customer-portal-api POST /api/questions and replied through the
  //   dedicated /api/portal/questions/:id/reply boundary. Pull-only.
  customer_questions: 'customerQuestions',
} as const;

/**
 * Local-only tenant Dexie tables — intentionally EXCLUDED from
 * `TENANT_COLLECTION_MAP` (and therefore from `/sync` pull/push and the
 * WebSocket sync validator). These tables exist in the tenantDb schema (see
 * `web/packages/local-db/src/databases/tenantDb.ts` v39+) but are never
 * replicated to MongoDB. Each is a client-side cache of data that is either
 * fetched on demand from a dedicated API endpoint or is transient UI state.
 *
 * Listing them here keeps the sync config self-documenting: a reader of
 * `TENANT_COLLECTION_MAP` can see not only what IS synced, but also confirm
 * that the absence of these tables is deliberate and why.
 *
 * Rationale per table:
 * - `update_token_usage` — local cache of update-token entitlement + usage
 *   data (Phase 8). `updateTokenService` writes here after each
 *   check-update / run-update API call so the Parcel Data Settings UI can
 *   display token balances without re-fetching. Server-authoritative ledger;
 *   client only mirrors the latest snapshot.
 * - `api_usage_cache`   — generic client-side cache for ad-hoc API response
 *   memoization (fetchedAt-stamped). Transient; no server counterpart.
 *
 * If any of these tables ever needs to become server-synced, add it to
 * `TENANT_COLLECTION_MAP` (and `TENANT_PUSH_ALLOWED_COLLECTIONS` if the
 * client should push), add a display name to
 * `TENANT_COLLECTION_DISPLAY_NAMES`, and remove it from this list.
 */
export const TENANT_LOCAL_ONLY_TABLES = [
  'update_token_usage',
  'api_usage_cache',
  // complex_mutations: local-only outbox for the Phase 1B local-first
  // complex-mutation service — pending operations are queued here before
  // POST /api/complex-mutations and retained for retry; never synced.
  'complex_mutations',
] as const;

/**
 * Tenant collections that are allowed to be pushed from client to server.
 * Collections NOT in this set are server-authoritative (read-only on client).
 *
 * NOTE on lookups (Phase 0 drift D-3 resolution):
 *   `lookups`, `lookupGroups`, `lookupCategories` ARE pushable — tenants own
 *   their department lookup data and may create, edit, and hide lookup values.
 *   HOWEVER, the tenant-api push handler enforces a per-document guard:
 *     - Documents with `originScope === 'system'` AND
 *       `inheritancePolicy === 'locked_mandatory'` are silently dropped.
 *       These are system-controlled values that the propagation process
 *       owns; the nightly replay would re-overwrite them anyway.
 *     - Documents with `originScope === 'system'` AND
 *       `inheritancePolicy === 'seed_hideable'` ARE accepted — tenants
 *       may customise seeded values.
 *     - Tenant-created documents (no `inheritedFromId` / `originScope`
 *       not 'system') are accepted freely.
 *   The guard is in `api/tenant-api/src/routes/syncRoutes.ts`
 *   `filterLockedSystemLookupChanges()`. The UI also prevents edits to
 *   locked documents; the server guard is defense-in-depth.
 */
export const TENANT_PUSH_ALLOWED_COLLECTIONS = new Set([
  'inspectableEntities',
  'inspectionCases',
  'contacts',
  'templates',
  'forms',
  'violationCodeSets',
  'violationCodeSetChapters',
  'violationIndex',
  'inspectionAccounts',
  'inspectionAccountGroups',
  'serviceAreaTypes',
  'serviceAreaValues',
  // Lookup collections are pushable; server enforces locked_mandatory guard.
  'lookups',
  'lookupGroups',
  'lookupCategories',
  'fieldDefinitions',
  'occupancyChemicals',
  'occupancyTanks',
  // [PHASE-1] importTemplates / importRuns removed — server-authoritative in system DB.
  // [D-5] users intentionally absent — all user mutations go through /system-sync/push
  //       (system DB, not tenant DB). See TENANT_SYSTEM_DERIVED_PUSH_ALLOWED_COLLECTIONS.
  // roles / user_groups / inspector_locations are intentionally absent:
  //   roles               — server-managed RBAC definitions; clients pull for display only.
  //   user_groups         — server-managed; tenants cannot mutate directly.
  //   inspector_locations — written by mobile-pwa via dedicated tracking endpoint.
  'reportDefinitions',
  'reportTemplates',
  'reportSchedules',
  'emailTemplates',
  'mailJobs',
  'inspectionDeliveryRecords',
  'inspectionDeliveryGroups',
  'inspectionDeliveryEvents',
  'inspectionPrintBatches',
  'generatedDocumentVersions',
  'inspectionSmsDeliveries',
  'inspectionPostageUsage',
  'auditLogs',
  // Inspection Programs & Entity Scheduling (#737 Phase 1) — tenant-owned
  // program/rule writes. scheduleExecutionRuns + scheduleExecutionEvidence
  // are intentionally absent: worker-written server-authoritative history.
  'inspectionPrograms',
  'autoScheduleRules',
  'carryForwardRules',
]);

/**
 * Tenant system-derived collections (synced from system DB)
 * Maps Dexie table name → system DB collection name
 */
export const TENANT_SYSTEM_DERIVED_COLLECTION_MAP = {
  system_tenant_ref: 'tenants',
  system_users_ref: 'users',
  system_inspection_templates: 'systemInspectionTemplates',
  system_forms: 'systemForms',
  // [PHASE-1] system_import_templates is filtered by visibility
  // (system-only | all-departments | specific-tenants) in
  // systemLibraryVisibility.ts before projection to tenants.
  system_import_templates: 'importTemplates',
  // [PHASE-1] system_import_runs is filtered by { tenantId } so a tenant
  // only ever sees its own runs. Server-enforced; not spoofable from the client.
  system_import_runs: 'importRuns',
  system_report_definitions: 'reportDefinitions',
  system_report_templates: 'reportTemplates',
  system_email_templates: 'emailTemplates',
} as const;

/**
 * Collections allowed to be pushed for system-derived sync
 */
export const TENANT_SYSTEM_DERIVED_PUSH_ALLOWED_COLLECTIONS = new Set(['tenants', 'users']);

/**
 * Human-readable display names for sync UI
 */
export const TENANT_COLLECTION_DISPLAY_NAMES: Record<string, string> = {
  entities: 'Entities',
  cases: 'Cases',
  contacts: 'Contacts',
  templates: 'Templates',
  forms: 'Forms',
  violation_code_sets: 'Violation Codes',
  violation_chapters: 'Violation Chapters',
  violation_index: 'Violation Index',
  inspection_accounts: 'Inspection Accounts',
  inspection_account_groups: 'Account Groups',
  roles: 'Roles',
  service_area_types: 'Service Area Types',
  service_area_values: 'Service Area Values',
  lookup_values: 'Lookups',
  lookup_groups: 'Lookup Groups',
  lookup_categories: 'Lookup Categories',
  field_definitions: 'Field Definitions',
  occupancy_chemicals: 'Occupancy Chemicals',
  occupancy_tanks: 'Occupancy Tanks',
  user_groups: 'User Groups',
  // [D-5] users removed — synced via system_users_ref / /system-sync.
  // [PHASE-1] import_templates / import_runs no longer tenant-owned.
  report_definitions: 'Report Definitions',
  report_templates: 'Report Templates',
  report_schedules: 'Report Schedules',
  uploaded_files: 'Uploaded Files',
  generated_documents: 'Generated Documents',
  email_templates: 'Email Templates',
  email_delivery_logs: 'Email Delivery Logs',
  mail_configurations: 'Mail Configurations',
  mail_jobs: 'Mail Jobs',
  inspection_delivery_records: 'Inspection Delivery Records',
  inspection_delivery_groups: 'Inspection Delivery Groups',
  inspection_delivery_events: 'Inspection Delivery Events',
  inspection_print_batches: 'Inspection Print Batches',
  generated_document_versions: 'Generated Document Versions',
  inspection_sms_deliveries: 'Inspection Text Messages',
  inspection_postage_usage: 'Inspection Postage Usage',
  inspector_locations: 'Inspector Locations',
  audit_logs: 'Audit Logs',
  background_jobs: 'Background Jobs',
  fee_schedules: 'Fee Schedules',
  system_tenant_ref: 'Department Settings',
  system_users_ref: 'System Users',
  system_inspection_templates: 'System Templates',
  system_forms: 'System Forms',
  system_import_templates: 'System Import Templates',
  system_import_runs: 'Import Runs',
  system_report_definitions: 'System Report Definitions',
  system_report_templates: 'System Report Templates',
  system_email_templates: 'System Email Templates',
  // complex_entity_groups: Complex Inspections Phase 1B — pull-only group table.
  complex_entity_groups: 'Complex Entity Groups',
  inspection_programs: 'Inspection Programs',
  auto_schedule_rules: 'Auto-Schedule Rules',
  carry_forward_rules: 'Carry-Forward Rules',
  schedule_execution_runs: 'Schedule Execution Runs',
  schedule_execution_evidence: 'Schedule Execution Evidence',
  schedule_rule_catalog: 'Schedule Rule Catalog',
  portal_requests: 'Portal Requests',
  portal_change_requests: 'Portal Change Requests',
  portal_activity_log: 'Portal Activity Log',
  self_certification_submissions: 'Self-Certification Submissions',
  public_safety_surveys: 'Public Safety Surveys',
  customer_questions: 'Customer Questions',
};
